/**
 * ModuleBuilderPage — F12.A T12.A8 / F12.B T12.B.1.
 *
 * Drag&drop kreator formularzy modułu custom.
 * Routing: `/modules/:id/builder`.
 *
 * F12.B T12.B.1 — multi-section:
 *  - Wiele sekcji z drag&drop (sekcje sortowalne na poziomie canvas)
 *  - Dodaj sekcję / usuń sekcję (gdy pusta) / edytuj właściwości sekcji
 *  - Prawy panel: właściwości sekcji (tytuł, kolumny, showHeader/showBorder/collapsible)
 *  - Elementy przeciągalne między sekcjami (cross-section via przycisk "Przenieś do...")
 *
 * NEG (deferred):
 *  - Mobile layouts
 *  - StylePanel (CSS customization) — F12.B T12.B.6
 *  - Drag krzyżowy pól między sekcjami (cross-section dnd) — SellCall-style, deferred
 *  - Preview renderowania formularza
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  type DragStartEvent,
  useDraggable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  GripVertical,
  LayoutGrid,
  Plus,
  Save,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FieldRegistry, type ModuleField } from '@/components/fields/FieldRegistry';
import {
  LayoutElementRegistry,
  tryRenderLayoutElement,
} from '@/components/fields/LayoutElementRegistry';
import { exportFormLayout } from '@/components/form-transfer/form-transfer-api';
import { FormImportModal } from '@/components/form-transfer/FormImportModal';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import {
  BuilderSettingsPanel,
  type LayoutSettings,
} from '@/components/modules/BuilderSettingsPanel';
import { FieldMetaEditor } from '@/components/modules/FieldMetaEditor';
import { FormTabsBar, uniqueTabSlug } from '@/components/modules/FormTabsBar';
import { LayoutElementsPalette } from '@/components/modules/LayoutElementsPalette';
import { LayoutFormModal, type LayoutFormValues } from '@/components/modules/LayoutFormModal';
import { type LayoutOption, LayoutSwitcher } from '@/components/modules/LayoutSwitcher';
import { PaletteSearchInput } from '@/components/modules/PaletteSearchInput';
import { type FieldOption, SelectOptionsEditor } from '@/components/modules/SelectOptionsEditor';
import { styleToCSS } from '@/components/modules/style/styleToCSS';
import { StylePanel } from '@/components/modules/StylePanel';
import { ValidationRulesEditor } from '@/components/modules/ValidationRulesEditor';
import { Spinner } from '@/components/Spinner';
import { useTabLabel } from '@/components/tabs/useTabLabel';
import { useToast } from '@/contexts/ToastContext';
import {
  type ElementDragData,
  type PaletteDragData,
  type SectionDragData,
  usePaletteDnD,
} from '@/hooks/usePaletteDnD';
import { reservedFieldSlugRenameDescription } from '@/hooks/useReservedFieldSlugToast';
import { extractErrorCode } from '@/lib/errors';
import { normalizeSearch } from '@/lib/search-normalize';

import {
  type ConditionalOperator,
  type FormLayoutTab,
  type LayoutElement,
  type LayoutSection,
  type ModuleBuilderAdapter,
  useTenantModuleBuilderAdapter,
} from './module-builder-adapter';

// Typy stanu layoutu (LayoutSection/LayoutElement/CustomModuleLayout) przeniesione do
// module-builder-adapter.ts (hub Krok 2) — strona i adaptery dzielą jedno źródło kształtu.
export type { ConditionalOperator } from './module-builder-adapter';

// ─── Selection state ─────────────────────────────────────────────────────────

type SelectedTarget =
  | { kind: 'section'; sectionId: string }
  | { kind: 'element'; elementId: string }
  | null;

// ─── Constants ───────────────────────────────────────────────────────────────

/** F12.A2 T12.A2.1 — typy które renderują editor opcji w prawym panelu builder */
const OPTIONS_AWARE_TYPES = new Set(['select', 'multiselect', 'radio', 'checkboxgroup', 'tags']);

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Tekst',
  textarea: 'Tekst długi',
  email: 'Email',
  phone: 'Telefon',
  url: 'URL',
  number: 'Liczba',
  decimal: 'Liczba dziesiętna',
  currency: 'Kwota (PLN)',
  date: 'Data',
  datetime: 'Data i godzina',
  select: 'Wybór z listy',
  multiselect: 'Multi-wybór',
  checkbox: 'Tak/Nie',
  radio: 'Wybór radio',
  file: 'Plik',
  image: 'Obrazek',
  relation: 'Relacja',
  user: 'Użytkownik',
  country: 'Kraj',
  autonumber: 'Auto-numer',
  // F12.B batch 1
  toggle: 'Przełącznik',
  rating: 'Ocena',
  percent: 'Procent',
  time: 'Godzina',
  daterange: 'Zakres dat',
  color: 'Kolor',
  slider: 'Suwak',
  hidden: 'Ukryte',
  password: 'Hasło',
  tags: 'Tagi',
  // F12.B batch 2
  checkboxgroup: 'Grupa checkboxów',
  richtext: 'Tekst sformatowany',
  markdown: 'Markdown',
  code: 'Kod',
  json: 'JSON',
  address: 'Adres',
  barcode: 'Kod kreskowy',
  qrcode: 'Kod QR',
  icon: 'Ikona',
  primaryattachment: 'Główny załącznik',
  // F12.B batch 3
  signature: 'Podpis',
  gallery: 'Galeria',
  audio: 'Audio',
  video: 'Wideo',
  location: 'Lokalizacja',
  formula: 'Formuła',
  lookup: 'Lookup',
  rollup: 'Rollup',
};

// ─── Page root ──────────────────────────────────────────────────────────────

interface ModuleBuilderPageProps {
  /** Gdy renderowane wewnątrz ModuleSettingsPage — ukrywa nagłówek strony i back button. */
  embedded?: boolean;
  /** Override moduleId z propsa (gdy embedded). Domyślnie z URL params. */
  moduleId?: string;
  /**
   * Zewnętrzny adapter źródła danych + zapisu (hub Krok 2 — projektowanie widoku `form`
   * szablonu). Brak = dotychczasowe zachowanie (adapter tenantowy z `moduleId`).
   * W trybie `template` ukryte są: LayoutSwitcher (kategorie/role/multi-layout to byty
   * tenanta), panel „Ustawienia" layoutu, dodawanie/usuwanie pól i edytory meta pola.
   */
  adapter?: ModuleBuilderAdapter;
}

export function ModuleBuilderPage({
  embedded,
  moduleId: moduleIdProp,
  adapter: adapterProp,
}: ModuleBuilderPageProps = {}) {
  const params = useParams<{ id: string }>();
  const moduleId = moduleIdProp ?? params.id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [sections, setSections] = useState<LayoutSection[]>([]);
  // Krok 8 — zakładki formularza: rejestr + aktywna. Pusty rejestr = stary, płaski układ.
  const [formTabs, setFormTabs] = useState<FormLayoutTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [pendingDeleteTab, setPendingDeleteTab] = useState<FormLayoutTab | null>(null);
  // T12.H.10 cz.C — globalne settings layoutu (szerokość, odstępy, etykiety, top-tabs toggle)
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({});
  const [selected, setSelected] = useState<SelectedTarget>(null);
  // T12.H.9 — 3 sub-taby: Treść / Styl / Zaawansowane (zamiast 2: props/style).
  // Conditional visibility przeniesione z 'content' do 'advanced'.
  const [rightPanelTab, setRightPanelTab] = useState<'content' | 'style' | 'advanced'>('content');
  /** T12.H.10 — left panel tab switcher: pola modułu vs paleta layout elementów. */
  const [leftPanelTab, setLeftPanelTab] = useState<'fields' | 'elements'>('fields');
  /** Wyszukiwarka pól w lewym panelu (parytet z paletą kartotek w MenuPalette). */
  const [fieldQuery, setFieldQuery] = useState('');
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** T12.H.11 — bieżący edytowany layout (null = pierwszy form layout, lub null gdy brak). */
  const [currentLayoutId, setCurrentLayoutId] = useState<string | null>(null);
  // Import layoutu formularza z pliku (przeniesienie z innego tenanta).
  const [formImportOpen, setFormImportOpen] = useState(false);
  const [showLayoutFormModal, setShowLayoutFormModal] = useState(false);
  // T12.H.11 — edycja meta + soft delete istniejącego layoutu (menu kontekstowe w switcherze)
  const [editingLayout, setEditingLayout] = useState<LayoutOption | null>(null);
  const [pendingDeleteLayout, setPendingDeleteLayout] = useState<LayoutOption | null>(null);
  // T12.H.10 cz.C — side panel ustawień całego layoutu
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  // ID sekcji do której ma być dodane nowe pole (null = pierwsza sekcja)
  const [addFieldTargetSectionId, setAddFieldTargetSectionId] = useState<string | null>(null);

  // Hub Krok 2 — źródło danych + zapis przez adapter. Adapter tenantowy montowany
  // zawsze (stała kolejność hooków), ale z zewnętrznym adapterem jego zapytania są
  // wyłączone — w scope admin nie strzela NIC tenantowego (w tym `/role-templates`).
  const tenantAdapter = useTenantModuleBuilderAdapter(moduleId, { enabled: !adapterProp });
  const adapter = adapterProp ?? tenantAdapter;
  const isTemplateMode = adapter.mode === 'template';
  // Karta samodzielnego kreatora kartoteki tenanta: nazwa kartoteki zamiast etykiety ze ścieżki.
  useTabLabel(
    !embedded && !adapterProp && adapter.module
      ? t('tenant.modules.builder.title', { name: adapter.module.name })
      : null,
  );

  // Eksport layoutu formularza do pliku (przeniesienie do innego tenanta).
  async function handleExportLayout(layout: LayoutOption) {
    try {
      await exportFormLayout(`/modules/${moduleId}/layouts`, layout.id);
      toast.show({
        variant: 'success',
        title: t('tenant.formTransfer.exported', { name: layout.name }),
      });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.formTransfer.exportError') });
    }
  }

  /**
   * T12.H.11 — wybiera bieżący edytowany layout.
   * Strategia: currentLayoutId (jeśli ustawione) → fallback do pierwszego
   * form+isDefault → fallback do pierwszego form. Null gdy moduł nie ma żadnego.
   */
  const allFormLayouts = useMemo(() => {
    return adapter.module?.layouts.filter((l) => l.layoutType === 'form') ?? [];
  }, [adapter.module]);

  const formLayout = useMemo(() => {
    if (currentLayoutId) {
      const explicit = allFormLayouts.find((l) => l.id === currentLayoutId);
      if (explicit) return explicit;
    }
    return (
      allFormLayouts.find((l) => l.isDefault && l.categoryId === null) ??
      allFormLayouts.find((l) => l.isDefault) ??
      allFormLayouts[0] ??
      null
    );
  }, [allFormLayouts, currentLayoutId]);

  // Inicjalizacja sections z layoutu (przy zmianie layoutu — id lub po refetch)
  useEffect(() => {
    if (formLayout) {
      const cfg = formLayout.layoutConfig;
      setSections(cfg.sections.length > 0 ? migrateOldSections(cfg.sections) : defaultSections());
      // Krok 8 — rejestr zakładek z layoutConfig.tabs (brak klucza = płaski układ).
      const tabs = cfg.tabs ?? [];
      setFormTabs(tabs);
      setActiveTabId(tabs[0]?.id ?? null);
      // T12.H.10 cz.C — załaduj globalne settings z layoutConfig.settings (jeśli istnieją)
      setLayoutSettings((cfg as { settings?: LayoutSettings }).settings ?? {});
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formLayout?.id]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!formLayout) throw new Error('No layout');
      await adapter.saveLayout(formLayout.id, {
        sections,
        ...(formTabs.length > 0 ? { tabs: formTabs } : {}),
        settings: layoutSettings,
      });
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.builder.toasts.saved') });
      void adapter.invalidate();
      setDirty(false);
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: adapter.saveErrorMessage?.(err) ?? t('tenant.modules.builder.toasts.saveError'),
      });
    },
  });

  // T12.H.11 — tworzenie nowego layoutu z modala (tenant only — UI gated)
  const createLayoutMutation = useMutation({
    mutationFn: async (values: LayoutFormValues) => {
      if (!adapter.createLayout) throw new Error('Unsupported in this mode');
      // pusty layout z 1 sekcją
      return adapter.createLayout(values, { sections: defaultSections() });
    },
    onSuccess: (newLayout) => {
      toast.show({ variant: 'success', title: 'Layout utworzony' });
      setShowLayoutFormModal(false);
      // Przełącz na nowo utworzony layout (refetch + select)
      void Promise.resolve(adapter.invalidate()).then(() => {
        setCurrentLayoutId(newLayout.id);
      });
    },
    onError: () => {
      toast.show({ variant: 'error', title: 'Nie udało się utworzyć layoutu' });
    },
  });

  // T12.H.11 — edycja meta istniejącego layoutu (nazwa / kategoria / isDefault)
  const updateLayoutMetaMutation = useMutation({
    mutationFn: async (input: { layoutId: string; values: LayoutFormValues }) => {
      if (!adapter.updateLayoutMeta) throw new Error('Unsupported in this mode');
      await adapter.updateLayoutMeta(input.layoutId, input.values);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: 'Layout zaktualizowany' });
      setEditingLayout(null);
      void adapter.invalidate();
    },
    onError: () => {
      toast.show({ variant: 'error', title: 'Nie udało się zapisać zmian' });
    },
  });

  // T12.H.11 — szybkie „ustaw jako domyślny" bez otwierania modala
  const setLayoutDefaultMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      if (!adapter.setLayoutDefault) throw new Error('Unsupported in this mode');
      await adapter.setLayoutDefault(layoutId);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: 'Ustawiono jako domyślny' });
      void adapter.invalidate();
    },
    onError: () => {
      toast.show({ variant: 'error', title: 'Nie udało się ustawić domyślnego' });
    },
  });

  const deleteLayoutMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      if (!adapter.deleteLayout) throw new Error('Unsupported in this mode');
      await adapter.deleteLayout(layoutId);
    },
    onSuccess: (_, layoutId) => {
      toast.show({ variant: 'success', title: 'Layout usunięty' });
      setPendingDeleteLayout(null);
      // Gdy usunięty był aktywny — przełącz na pierwszy pozostały (refetch zrobi resztę)
      if (currentLayoutId === layoutId) {
        setCurrentLayoutId(null);
      }
      void adapter.invalidate();
    },
    onError: () => {
      toast.show({ variant: 'error', title: 'Nie udało się usunąć layoutu' });
    },
  });

  const addFieldMutation = useMutation({
    mutationFn: async (input: { name: string; fieldType: string; isRequired: boolean }) => {
      if (!adapter.addField) throw new Error('Unsupported in this mode');
      return adapter.addField(input);
    },
    onSuccess: (newField) => {
      toast.show({ variant: 'success', title: t('tenant.modules.builder.toasts.fieldAdded') });
      void adapter.invalidate();
      setShowAddFieldModal(false);
      addElementToCanvas(newField, addFieldTargetSectionId ?? undefined);
      setAddFieldTargetSectionId(null);
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.modules.builder.toasts.fieldAddError'),
        // Nazwa techniczna zarezerwowana przez system (np. „decisions”) — czytelny opis zamiast kodu;
        // błąd bez kodu (np. brak sieci) — bez opisu, sam tytuł.
        description: reservedFieldSlugRenameDescription(err, t) ?? extractErrorCode(err),
      });
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      if (!adapter.deleteField) throw new Error('Unsupported in this mode');
      await adapter.deleteField(fieldId);
      return fieldId;
    },
    onSuccess: (fieldId) => {
      toast.show({ variant: 'success', title: t('tenant.modules.builder.toasts.fieldDeleted') });
      void adapter.invalidate();
      setSections((prev) =>
        prev.map((s) => ({ ...s, elements: s.elements.filter((e) => e.fieldId !== fieldId) })),
      );
      setDirty(true);
    },
    onError: (err) => {
      // Krok 6: pole z szablonu jest chronione po stronie backendu (403) — komunikat zamiast generyka.
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.show({
        variant: 'error',
        title:
          code === 'TEMPLATE_FIELD_PROTECTED'
            ? t('tenant.modules.builder.toasts.fieldTemplateProtected')
            : t('tenant.modules.builder.toasts.fieldDeleteError'),
      });
    },
  });

  // ─── Section operations ─────────────────────────────────────────────────

  // ─── Zakładki formularza (Krok 8) ────────────────────────────────────────

  /** Aktywna zakładka po walidacji rejestru (null = tryb płaski, bez zakładek). */
  const resolvedActiveTabId = useMemo(() => {
    if (formTabs.length === 0) return null;
    return formTabs.some((tab) => tab.id === activeTabId) ? activeTabId : formTabs[0]!.id;
  }, [formTabs, activeTabId]);

  /** Sekcje widoczne na canvasie — przy zakładkach tylko te z aktywnej. */
  const visibleSections = useMemo(() => {
    if (resolvedActiveTabId === null) return sections;
    const known = new Set(formTabs.map((tab) => tab.id));
    const firstId = formTabs[0]!.id;
    return sections.filter(
      (s) => (s.tabId && known.has(s.tabId) ? s.tabId : firstId) === resolvedActiveTabId,
    );
  }, [sections, formTabs, resolvedActiveTabId]);

  function addFormTab() {
    const taken = new Set(formTabs.map((tab) => tab.slug));
    const newTitle = t('tenant.modules.builder.tabs.newTitle');
    if (formTabs.length === 0) {
      // Pierwsze użycie: dotychczasowe sekcje lądują w zakładce „Dane", nowa jest pusta.
      const first: FormLayoutTab = {
        id: crypto.randomUUID(),
        slug: uniqueTabSlug(t('tenant.modules.builder.tabs.defaultFirstTitle'), taken),
        title: t('tenant.modules.builder.tabs.defaultFirstTitle'),
      };
      taken.add(first.slug);
      const second: FormLayoutTab = {
        id: crypto.randomUUID(),
        slug: uniqueTabSlug(newTitle, taken),
        title: newTitle,
      };
      setSections((prev) => prev.map((s) => ({ ...s, tabId: first.id })));
      setFormTabs([first, second]);
      setActiveTabId(second.id);
    } else {
      const tab: FormLayoutTab = {
        id: crypto.randomUUID(),
        slug: uniqueTabSlug(newTitle, taken),
        title: newTitle,
      };
      setFormTabs((prev) => [...prev, tab]);
      setActiveTabId(tab.id);
    }
    setDirty(true);
  }

  function renameFormTab(tabId: string, title: string) {
    // Slug zostaje stary (stabilne deep-linki) — zmienia się tylko tytuł.
    setFormTabs((prev) => prev.map((tab) => (tab.id === tabId ? { ...tab, title } : tab)));
    setDirty(true);
  }

  function moveFormTab(tabId: string, direction: -1 | 1) {
    setFormTabs((prev) => {
      const idx = prev.findIndex((tab) => tab.id === tabId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      return arrayMove(prev, idx, target);
    });
    setDirty(true);
  }

  function removeFormTab(tabId: string) {
    const remaining = formTabs.filter((tab) => tab.id !== tabId);
    if (remaining.length === 0) {
      // Ostatnia zakładka → powrót do płaskiego układu (sekcje zostają, tabId znika).
      setSections((prev) => prev.map(({ tabId: _ignored, ...rest }) => rest));
      setFormTabs([]);
      setActiveTabId(null);
    } else {
      // Sekcje usuwanej zakładki przejmuje pierwsza pozostała — nic nie znika.
      const fallbackId = remaining[0]!.id;
      setSections((prev) => prev.map((s) => (s.tabId === tabId ? { ...s, tabId: fallbackId } : s)));
      setFormTabs(remaining);
      if (resolvedActiveTabId === tabId) setActiveTabId(fallbackId);
    }
    setDirty(true);
  }

  function addSection() {
    const newSection: LayoutSection = {
      ...makeEmptySection(),
      ...(resolvedActiveTabId !== null ? { tabId: resolvedActiveTabId } : {}),
    };
    setSections((prev) => [...prev, newSection]);
    setDirty(true);
    setSelected({ kind: 'section', sectionId: newSection.id });
  }

  function removeSection(sectionId: string) {
    setSections((prev) => {
      if (prev.length <= 1) return prev; // zawsze min 1 sekcja
      return prev.filter((s) => s.id !== sectionId);
    });
    if (selected?.kind === 'section' && selected.sectionId === sectionId) {
      setSelected(null);
    }
    setDirty(true);
  }

  function updateSection(
    sectionId: string,
    patch: Partial<Omit<LayoutSection, 'id' | 'elements'>>,
  ): void {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)));
    setDirty(true);
  }

  // ─── Element operations ─────────────────────────────────────────────────

  function addElementToCanvas(field: ModuleField, targetSectionId?: string) {
    const newEl: LayoutElement = {
      id: crypto.randomUUID(),
      type: 'field',
      fieldId: field.id,
      width: 1,
      settings: {},
    };
    // Krok 8: default trafia do pierwszej sekcji AKTYWNEJ zakładki (nie globalnie pierwszej).
    const fallbackTargetId = visibleSections[0]?.id;
    setSections((prev) => {
      const target = targetSectionId ?? fallbackTargetId;
      if (!target) {
        // Aktywna zakładka bez sekcji (lub pusty canvas) → nowa sekcja w tej zakładce.
        return [
          ...prev,
          {
            ...makeEmptySection(),
            ...(resolvedActiveTabId !== null ? { tabId: resolvedActiveTabId } : {}),
            elements: [newEl],
          },
        ];
      }
      if (!prev.some((s) => s.id === target)) return prev;
      return prev.map((s) => (s.id === target ? { ...s, elements: [...s.elements, newEl] } : s));
    });
    setDirty(true);
    setSelected({ kind: 'element', elementId: newEl.id });
  }

  /**
   * T12.H.10 — Dodaje layout element (sekcja/karta/zakładki/alert/itd.)
   * z palety LayoutElementsPalette do canvas. Bierze typ + pobiera
   * defaultSettings z LayoutElementRegistry.
   */
  function addLayoutElementToCanvas(type: string, targetSectionId?: string) {
    const defaultSettings = LayoutElementRegistry.defaultSettings(type);
    const newEl: LayoutElement = {
      id: crypto.randomUUID(),
      type,
      // Layout elementy NIE mają fieldId — pola odwołują się do custom_module_fields
      // przez fieldId, layout elementy żyją tylko w layout_config JSONB.
      width: 1,
      settings: defaultSettings,
    };
    // Krok 8: default trafia do pierwszej sekcji AKTYWNEJ zakładki (nie globalnie pierwszej).
    const fallbackTargetId = visibleSections[0]?.id;
    setSections((prev) => {
      const target = targetSectionId ?? fallbackTargetId;
      if (!target) {
        return [
          ...prev,
          {
            ...makeEmptySection(),
            ...(resolvedActiveTabId !== null ? { tabId: resolvedActiveTabId } : {}),
            elements: [newEl],
          },
        ];
      }
      if (!prev.some((s) => s.id === target)) return prev;
      return prev.map((s) => (s.id === target ? { ...s, elements: [...s.elements, newEl] } : s));
    });
    setDirty(true);
    setSelected({ kind: 'element', elementId: newEl.id });
  }

  function removeElement(elementId: string) {
    setSections((prev) =>
      prev.map((s) => ({ ...s, elements: s.elements.filter((e) => e.id !== elementId) })),
    );
    if (selected?.kind === 'element' && selected.elementId === elementId) setSelected(null);
    setDirty(true);
  }

  function updateElement(elementId: string, patch: Partial<LayoutElement>) {
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        elements: s.elements.map((e) => (e.id === elementId ? { ...e, ...patch } : e)),
      })),
    );
    setDirty(true);
  }

  function updateElementSettings(elementId: string, patch: Partial<LayoutElement['settings']>) {
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        elements: s.elements.map((e) =>
          e.id === elementId ? { ...e, settings: { ...e.settings, ...patch } } : e,
        ),
      })),
    );
    setDirty(true);
  }

  function moveElementToSection(elementId: string, targetSectionId: string) {
    setSections((prev) => {
      let movedEl: LayoutElement | undefined;
      const withoutEl = prev.map((s) => {
        const el = s.elements.find((e) => e.id === elementId);
        if (el) movedEl = el;
        return { ...s, elements: s.elements.filter((e) => e.id !== elementId) };
      });
      if (!movedEl) return prev;
      return withoutEl.map((s) =>
        s.id === targetSectionId ? { ...s, elements: [...s.elements, movedEl!] } : s,
      );
    });
    setDirty(true);
  }

  // ─── Drag&drop (F12.N D2: jeden top-level DndContext przez usePaletteDnD) ──

  function reorderSections(activeId: string, overId: string) {
    setSections((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === activeId);
      const newIdx = prev.findIndex((s) => s.id === overId);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
    setDirty(true);
  }

  function reorderElementsInSection(
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) {
    // ModuleBuilderPage (modułów własnych) — MVP wspiera tylko same-container reorder.
    // Cross-container (Sub-3) w SystemModuleBuilderPage; tu noop gdy source ≠ target.
    if (sourceContainerId !== targetContainerId) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sourceContainerId) return s;
        const oldIdx = s.elements.findIndex((e) => e.id === activeId);
        const newIdx = s.elements.findIndex((e) => e.id === overId);
        if (oldIdx < 0 || newIdx < 0) return s;
        return { ...s, elements: arrayMove(s.elements, oldIdx, newIdx) };
      }),
    );
    setDirty(true);
  }

  const { sensors, onDragEnd } = usePaletteDnD({
    onAddFromPalette: (kind, payload, sectionId) => {
      if (kind === 'field') {
        // Klocek to pole modułu (`payload` = fieldId). Reużycie istniejącej funkcji.
        // Bierzemy fields bezpośrednio z adaptera (deklaracja `activeFields` jest dopiero po
        // render guards — closure nie obejmuje, więc szukamy w surowych danych modułu).
        const field = adapter.module?.fields.find((f) => f.id === payload);
        if (field) addElementToCanvas(field, sectionId);
      } else {
        // Klocek to layout element (np. „card", „alert" — `payload` = type).
        addLayoutElementToCanvas(payload, sectionId);
      }
    },
    onSectionReorder: reorderSections,
    onElementReorder: reorderElementsInSection,
    onAccordionPanelReorder: () => {
      // ModuleBuilderPage (modułów własnych) nie ma akordeonu w canvasie w MVP —
      // pakiet B Sub-2 dotyczy tylko SystemModuleBuilderPage. Noop tu nieblokujący.
    },
    onStepReorder: () => {
      // Stepper/wizard też niewspierany w modułach własnych w MVP — Etap 3 T2.
    },
  });

  // PR 1 commit 8 (2026-05-18) — parytet z SystemModuleBuilderPage (PR 1603 fix
  // a33d639): DragOverlay pokazuje „ducha" jadącego za kursorem podczas drag
  // pola/elementu z canvasu albo z palety. Bez tego sortable klocek tylko
  // reorganizuje sąsiedztwo (transform + opacity), brak feedbacku wizualnego.
  type ActiveDragInfo =
    | { kind: 'palette'; data: PaletteDragData }
    | { kind: 'element'; id: string }
    | { kind: 'section'; id: string };
  const [activeDrag, setActiveDrag] = useState<ActiveDragInfo | null>(null);
  const onDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { source?: string } | undefined;
    if (!data?.source) return;
    if (data.source === 'palette') {
      setActiveDrag({ kind: 'palette', data: data as unknown as PaletteDragData });
    } else if (data.source === 'element') {
      setActiveDrag({ kind: 'element', id: String(event.active.id) });
    } else if (data.source === 'section') {
      setActiveDrag({ kind: 'section', id: String(event.active.id) });
    }
  };
  const onDragEndWrapped = (event: Parameters<typeof onDragEnd>[0]) => {
    onDragEnd(event);
    setActiveDrag(null);
  };

  // ─── Render guards ───────────────────────────────────────────────────────

  if (!adapterProp && !moduleId) {
    return <main className="p-6">Brak ID modułu</main>;
  }

  if (adapter.isLoading) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner />
      </main>
    );
  }

  if (adapter.isError || !adapter.module) {
    return (
      <main className="bg-bg-secondary p-6">
        <Card padding="md">
          <p className="text-error-500">{t('tenant.modules.builder.loadError')}</p>
        </Card>
      </main>
    );
  }

  const module = adapter.module;
  const activeFields = module.fields.filter(
    (f) => !('gcrecord' in f && (f as { gcrecord?: string }).gcrecord),
  );
  const usedFieldIds = new Set(sections.flatMap((s) => s.elements.map((e) => e.fieldId)));
  // Filtr wyszukiwarki po nazwie/slugu (diakrytyka-insensitive).
  const fieldQ = normalizeSearch(fieldQuery.trim());
  const fieldMatches = (f: ModuleField) =>
    !fieldQ || normalizeSearch(`${f.name} ${f.slug}`).includes(fieldQ);
  const unusedFields = activeFields.filter((f) => !usedFieldIds.has(f.id) && fieldMatches(f));
  const usedFields = activeFields.filter((f) => usedFieldIds.has(f.id) && fieldMatches(f));

  const allElements = sections.flatMap((s) => s.elements);
  const selectedElement =
    selected?.kind === 'element' ? allElements.find((e) => e.id === selected.elementId) : undefined;
  const selectedField = selectedElement
    ? activeFields.find((f) => f.id === selectedElement.fieldId)
    : null;
  const selectedSection =
    selected?.kind === 'section' ? sections.find((s) => s.id === selected.sectionId) : undefined;

  const Wrapper = embedded ? 'div' : 'main';
  const wrapperClass = embedded ? '' : 'min-h-full bg-bg-secondary';

  return (
    <Wrapper className={wrapperClass} data-testid="module-builder-page">
      {/* Header — pomijany gdy embedded (hub ma swój nagłówek + zakładki) */}
      {!embedded && (
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border-primary bg-bg-primary px-6 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/modules')}
              aria-label={t('common.back')}
              className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-text-primary">
                {t('tenant.modules.builder.title', { name: module.name })}
              </h1>
              <p className="text-xs text-text-tertiary">/{module.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* T12.H.11 — switcher layoutów modułu (byty tenanta — ukryty w trybie szablonu) */}
            {!isTemplateMode && (
              <LayoutSwitcher
                layouts={allFormLayouts.map((l) => ({
                  id: l.id,
                  name: l.name,
                  categoryId: l.categoryId ?? null,
                  isDefault: l.isDefault,
                  layoutType: l.layoutType,
                  roleIds: l.roleIds ?? [],
                  menuSourceModuleId:
                    (l as { menuSourceModuleId?: string | null }).menuSourceModuleId ?? null,
                }))}
                currentLayoutId={formLayout?.id ?? null}
                categories={adapter.categories}
                roleTemplates={adapter.roleTemplates}
                onSelect={(layoutId) => setCurrentLayoutId(layoutId)}
                onCreate={() => setShowLayoutFormModal(true)}
                onManageCategories={() => navigate(`/modules/${moduleId}/settings?tab=types`)}
                onEdit={(l) => setEditingLayout(l)}
                onDelete={(l) => setPendingDeleteLayout(l)}
                onSetDefault={(l) => setLayoutDefaultMutation.mutate(l.id)}
                onExport={isTemplateMode ? undefined : (l) => void handleExportLayout(l)}
                onImport={isTemplateMode ? undefined : () => setFormImportOpen(true)}
              />
            )}
            {!isTemplateMode && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Settings2 className="h-4 w-4" />}
                onClick={() => setShowSettingsPanel(true)}
                data-testid="builder-settings-btn"
              >
                Ustawienia
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={addSection}
              data-testid="builder-add-section-btn"
            >
              {t('tenant.modules.builder.addSection')}
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={!dirty || saveMutation.isPending}
              data-testid="builder-save-btn"
            >
              {t('tenant.modules.builder.save')}
            </Button>
          </div>
        </header>
      )}

      {/* Belka akcji widoczna gdy embedded — bez nagłówka strony */}
      {embedded && (
        <div className="sticky top-0 z-10 flex items-center justify-end gap-2 border-b border-border-primary bg-bg-primary px-4 py-2">
          {/* T12.H.11 — switcher layoutów modułu w embedded toolbar (ukryty w trybie szablonu) */}
          {!isTemplateMode && (
            <LayoutSwitcher
              layouts={allFormLayouts.map((l) => ({
                id: l.id,
                name: l.name,
                categoryId: l.categoryId ?? null,
                isDefault: l.isDefault,
                layoutType: l.layoutType,
                menuSourceModuleId:
                  (l as { menuSourceModuleId?: string | null }).menuSourceModuleId ?? null,
              }))}
              currentLayoutId={formLayout?.id ?? null}
              categories={adapter.categories}
              onSelect={(layoutId) => setCurrentLayoutId(layoutId)}
              onCreate={() => setShowLayoutFormModal(true)}
              onEdit={(l) => setEditingLayout(l)}
              onDelete={(l) => setPendingDeleteLayout(l)}
              onSetDefault={(l) => setLayoutDefaultMutation.mutate(l.id)}
              onExport={isTemplateMode ? undefined : (l) => void handleExportLayout(l)}
              onImport={isTemplateMode ? undefined : () => setFormImportOpen(true)}
            />
          )}
          {!isTemplateMode && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Settings2 className="h-4 w-4" />}
              onClick={() => setShowSettingsPanel(true)}
              data-testid="builder-settings-btn"
            >
              Ustawienia
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={addSection}
            data-testid="builder-add-section-btn"
          >
            {t('tenant.modules.builder.addSection')}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Save className="h-4 w-4" />}
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!dirty || saveMutation.isPending}
            data-testid="builder-save-btn"
          >
            {t('tenant.modules.builder.save')}
          </Button>
        </div>
      )}

      {/* 3-column layout — F12.N D2: jeden top-level DndContext owija paletę + canvas,
          żeby palette item można było upuścić na sekcję. SortableContext per sekcja/per
          element zostaje wewnątrz (sort sekcji + sort elementów). */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEndWrapped}
        onDragCancel={() => setActiveDrag(null)}
      >
        <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[260px_1fr_280px]">
          {/* LEFT — fields list / layout elements palette */}
          <aside data-testid="builder-left-panel">
            <Card padding="md">
              {/* Tab switcher: Pola | Elementy (T12.H.10) */}
              <div className="mb-3 flex gap-1 border-b border-border-primary" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftPanelTab === 'fields'}
                  onClick={() => setLeftPanelTab('fields')}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                    leftPanelTab === 'fields'
                      ? 'border-b-2 border-accent-500 text-accent-600'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="builder-tab-fields"
                >
                  {t('tenant.modules.builder.fields.title')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftPanelTab === 'elements'}
                  onClick={() => setLeftPanelTab('elements')}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                    leftPanelTab === 'elements'
                      ? 'border-b-2 border-accent-500 text-accent-600'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="builder-tab-elements"
                >
                  Elementy
                </button>
              </div>

              {leftPanelTab === 'elements' ? (
                <LayoutElementsPalette
                  onAdd={(type) => addLayoutElementToCanvas(type)}
                  moduleSlug={adapter.paletteModuleSlug}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-text-primary">
                      {t('tenant.modules.builder.fields.title')}
                    </h2>
                    {/* Hub Krok 2: pola szablonu definiuje edytor struktury — builder ich nie dodaje */}
                    {adapter.addField && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Plus className="h-3 w-3" />}
                        onClick={() => {
                          setAddFieldTargetSectionId(null);
                          setShowAddFieldModal(true);
                        }}
                        data-testid="builder-add-field-btn"
                      >
                        {t('tenant.modules.builder.fields.add')}
                      </Button>
                    )}
                  </div>

                  {activeFields.length === 0 && (
                    <p className="text-xs text-text-tertiary">
                      {t('tenant.modules.builder.fields.empty')}
                    </p>
                  )}

                  {activeFields.length > 0 && (
                    <PaletteSearchInput
                      value={fieldQuery}
                      onChange={setFieldQuery}
                      placeholder={t('common.layoutPalette.searchFields')}
                      testId="builder-fields-search"
                    />
                  )}

                  {unusedFields.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-text-muted">
                        {t('tenant.modules.builder.fields.available')}
                      </p>
                      {unusedFields.map((field) => (
                        <FieldListItem
                          key={field.id}
                          field={field}
                          onClick={() => addElementToCanvas(field)}
                          onDelete={
                            adapter.deleteField
                              ? () => deleteFieldMutation.mutate(field.id)
                              : undefined
                          }
                          deleting={deleteFieldMutation.isPending}
                        />
                      ))}
                    </div>
                  )}

                  {usedFields.length > 0 && unusedFields.length > 0 && (
                    <hr className="my-3 border-border-primary" />
                  )}
                  {usedFields.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-text-muted">
                        {t('tenant.modules.builder.fields.onCanvas')}
                      </p>
                      {usedFields.map((field) => (
                        <FieldListItem
                          key={field.id}
                          field={field}
                          disabled
                          onDelete={
                            adapter.deleteField
                              ? () => deleteFieldMutation.mutate(field.id)
                              : undefined
                          }
                          deleting={deleteFieldMutation.isPending}
                        />
                      ))}
                    </div>
                  )}

                  {activeFields.length > 0 &&
                    unusedFields.length === 0 &&
                    usedFields.length === 0 && (
                      <p className="text-xs text-text-tertiary">
                        {t('common.layoutPalette.noFields')}
                      </p>
                    )}
                </>
              )}
            </Card>
            {/*
            T12.H.11 cleanup: lewa karta „Kategorie / liczba / Kategorie →"
            usunięta. Była myląca po dodaniu LayoutSwitcher (toolbar) który też
            mówi o kategoriach — user nie odróżniał „lista layoutów per kategoria"
            (switcher) od „zarządzaj listą kategorii w module" (karta). Link
            „Zarządzaj kategoriami →" przeniesiony do stopki dropdownu LayoutSwitcher,
            gdzie pasuje kontekstowo. Strona ModuleSettings → zakładka Kategorie
            nadal dostępna z hubu.
          */}
          </aside>

          {/* CENTER — canvas */}
          <section data-testid="builder-canvas">
            {/* Krok 8 — pasek „Zakładki formularza" (parytet z builderem modułów systemowych). */}
            <FormTabsBar
              tabs={formTabs}
              activeTabId={resolvedActiveTabId}
              onSelect={(id) => setActiveTabId(id)}
              onAdd={addFormTab}
              onRename={renameFormTab}
              onRequestDelete={(id) =>
                setPendingDeleteTab(formTabs.find((tab) => tab.id === id) ?? null)
              }
              onMove={moveFormTab}
            />
            <Card padding="md" className="min-h-[500px]">
              {/* F12.N D2: DndContext przeniesiony na top-level (wokół 3-col layout).
                Tu zostaje tylko SortableContext — top-level dispatcher
                (usePaletteDnD.onDragEnd) wywoła reorderSections gdy active.source='section'. */}
              <SortableContext
                items={visibleSections.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {visibleSections.map((section) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    fields={activeFields}
                    selected={selected}
                    onSelectSection={(id) => setSelected({ kind: 'section', sectionId: id })}
                    onSelectElement={(id) => setSelected({ kind: 'element', elementId: id })}
                    onRemoveElement={removeElement}
                    onRemoveSection={removeSection}
                    onAddFieldToSection={(sectionId) => {
                      setAddFieldTargetSectionId(sectionId);
                      setShowAddFieldModal(true);
                    }}
                    onMoveElement={moveElementToSection}
                    allSections={visibleSections}
                    canRemoveSection={sections.length > 1}
                  />
                ))}
              </SortableContext>

              {(visibleSections.length === 0 ||
                (visibleSections.every((s) => s.elements.length === 0) &&
                  visibleSections.length === 1)) && (
                <div
                  className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-primary text-text-muted mt-2"
                  data-testid="builder-canvas-empty"
                >
                  <p className="text-sm">{t('tenant.modules.builder.canvas.empty')}</p>
                  <p className="text-xs">{t('tenant.modules.builder.canvas.emptyHint')}</p>
                </div>
              )}
            </Card>
          </section>

          {/* RIGHT — properties */}
          <aside data-testid="builder-right-panel">
            <Card padding="md">
              {/* Nagłówek + taby (tylko gdy element zaznaczony) — T12.H.9: 3 sub-taby */}
              {selectedElement ? (
                <div className="flex gap-1 mb-3 border-b border-gray-200 dark:border-gray-700 -mx-4 px-4 pb-0">
                  <button
                    type="button"
                    onClick={() => setRightPanelTab('content')}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                      rightPanelTab === 'content'
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="builder-tab-content"
                  >
                    Treść
                  </button>
                  <button
                    type="button"
                    onClick={() => setRightPanelTab('style')}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                      rightPanelTab === 'style'
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="builder-tab-style"
                  >
                    Styl
                  </button>
                  <button
                    type="button"
                    onClick={() => setRightPanelTab('advanced')}
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                      rightPanelTab === 'advanced'
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="builder-tab-advanced"
                  >
                    Zaawansowane
                  </button>
                </div>
              ) : (
                <h2 className="mb-3 text-sm font-semibold text-text-primary">
                  {t('tenant.modules.builder.properties.title')}
                </h2>
              )}

              {/* Sekcja zaznaczona — w trybie szablonu kontrakt zna kolumny 1–3 */}
              {selectedSection && !selectedElement && (
                <SectionProperties
                  section={selectedSection}
                  columnsOptions={isTemplateMode ? [1, 2, 3] : undefined}
                  tabs={formTabs}
                  onUpdate={(patch) => updateSection(selectedSection.id, patch)}
                />
              )}

              {/* T12.H.9: pole zaznaczone → Treść = label/width/required (ElementProperties bez conditional) */}
              {selectedElement && selectedField && rightPanelTab === 'content' && (
                <>
                  <ElementProperties
                    element={selectedElement}
                    field={selectedField}
                    sections={sections}
                    allFields={activeFields}
                    onUpdateElement={(patch) => updateElement(selectedElement.id, patch)}
                    onUpdateSettings={(patch) => updateElementSettings(selectedElement.id, patch)}
                    onMoveToSection={(sectionId) =>
                      moveElementToSection(selectedElement.id, sectionId)
                    }
                  />
                  {/* Edytory meta pola — tenant only: piszą bezpośrednio w byty modułu
                      custom (`/modules/:id/fields/...`); meta pól szablonu edytuje
                      edytor struktury w hubie. */}
                  {!isTemplateMode &&
                    OPTIONS_AWARE_TYPES.has(selectedField.fieldType) &&
                    moduleId && (
                      <SelectOptionsEditor
                        moduleId={moduleId}
                        fieldId={selectedField.id}
                        fieldSlug={selectedField.slug}
                        initialOptions={(selectedField.options as FieldOption[] | undefined) ?? []}
                      />
                    )}
                  {!isTemplateMode && moduleId && (
                    <ValidationRulesEditor
                      moduleId={moduleId}
                      fieldId={selectedField.id}
                      fieldSlug={selectedField.slug}
                      fieldType={selectedField.fieldType}
                      initialRules={
                        (selectedField.validationRules as Record<string, string> | undefined) ?? {}
                      }
                    />
                  )}
                  {!isTemplateMode && moduleId && (
                    <FieldMetaEditor
                      moduleId={moduleId}
                      fieldId={selectedField.id}
                      fieldSlug={selectedField.slug}
                      fieldType={selectedField.fieldType}
                      initialShowInList={selectedField.showInList}
                      initialIsUnique={selectedField.isUnique}
                      initialDefaultValue={selectedField.defaultValue}
                      initialRelationModuleId={selectedField.relationModuleId}
                      initialRelationSystemModuleSlug={selectedField.relationSystemModuleSlug}
                      initialRelationDisplayField={selectedField.relationDisplayField}
                      initialRelationMultiple={selectedField.relationMultiple}
                      initialDependsOnFieldSlug={selectedField.dependsOnFieldSlug}
                      initialDependsOnOptionMap={selectedField.dependsOnOptionMap}
                      currentFieldOptions={selectedField.options}
                      siblingSelectFields={activeFields
                        .filter(
                          (f) =>
                            f.id !== selectedField.id &&
                            (f.fieldType === 'select' || f.fieldType === 'multiselect'),
                        )
                        .map((f) => ({ id: f.id, slug: f.slug, name: f.name, options: f.options }))}
                      initialCascadeFromMap={selectedField.cascadeFromMap}
                      allSiblingFields={activeFields
                        .filter((f) => f.id !== selectedField.id)
                        .map((f) => ({ id: f.id, slug: f.slug, name: f.name }))}
                    />
                  )}
                </>
              )}

              {/* T12.H.9: layout element (nie pole) → Treść = propertiesEditor z LayoutElementRegistry */}
              {selectedElement && !selectedField && rightPanelTab === 'content' && (
                <LayoutElementContentTab
                  element={selectedElement}
                  onUpdateElement={(patch) => updateElement(selectedElement.id, patch)}
                  onUpdateSettings={(patch) => updateElementSettings(selectedElement.id, patch)}
                />
              )}

              {/* T12.H.9: zakładka Styl (dla pól i layout elementów) */}
              {selectedElement && rightPanelTab === 'style' && (
                <StylePanel
                  style={selectedElement.style}
                  onChange={(newStyle) => updateElement(selectedElement.id, { style: newStyle })}
                />
              )}

              {/* T12.H.9: zakładka Zaawansowane — conditional visibility (wspólne dla pól i layoutu) */}
              {selectedElement && rightPanelTab === 'advanced' && (
                <ConditionalVisibilityPanel
                  element={selectedElement}
                  currentFieldId={selectedField?.id}
                  allFields={activeFields}
                  onUpdateSettings={(patch) => updateElementSettings(selectedElement.id, patch)}
                />
              )}

              {/* Nic nie zaznaczone */}
              {!selectedElement && !selectedSection && (
                <p className="text-xs text-text-tertiary">
                  {t('tenant.modules.builder.properties.empty')}
                </p>
              )}
            </Card>
          </aside>
        </div>

        {/* DragOverlay — parytet z SystemModuleBuilderPage (PR 1603 a33d639). */}
        <DragOverlay dropAnimation={null}>
          {activeDrag?.kind === 'palette' && (
            <ModuleBuilderPaletteGhost
              data={activeDrag.data}
              fields={adapter.module?.fields ?? []}
            />
          )}
          {activeDrag?.kind === 'element' && (
            <ModuleBuilderElementGhost
              elementId={activeDrag.id}
              sections={sections}
              fields={adapter.module?.fields ?? []}
            />
          )}
          {activeDrag?.kind === 'section' && (
            <ModuleBuilderSectionGhost sectionId={activeDrag.id} sections={sections} />
          )}
        </DragOverlay>
      </DndContext>

      {/* Add field modal — tylko gdy adapter wspiera dodawanie pól (tenant) */}
      {adapter.addField && (
        <AddFieldModal
          open={showAddFieldModal}
          onClose={() => setShowAddFieldModal(false)}
          onSubmit={(input) => addFieldMutation.mutate(input)}
          loading={addFieldMutation.isPending}
        />
      )}

      {/* T12.H.11 — modal tworzenia nowego layoutu (tenant only) */}
      {!isTemplateMode && moduleId && (
        <FormImportModal
          open={formImportOpen}
          onClose={() => setFormImportOpen(false)}
          endpointBase={`/modules/${moduleId}/layouts`}
          onImported={(id) => {
            void Promise.resolve(adapter.invalidate()).then(() => setCurrentLayoutId(id));
          }}
        />
      )}

      <LayoutFormModal
        open={showLayoutFormModal}
        mode="create"
        categories={adapter.categories}
        roleTemplates={adapter.roleTemplates}
        saving={createLayoutMutation.isPending}
        onSubmit={(values) => createLayoutMutation.mutate(values)}
        onClose={() => setShowLayoutFormModal(false)}
      />

      {/* T12.H.11 + T12.M.A.4 — modal edycji meta istniejącego layoutu (nazwa / kategoria / domyślny / role) */}
      <LayoutFormModal
        open={editingLayout !== null}
        mode="edit"
        categories={adapter.categories}
        roleTemplates={adapter.roleTemplates}
        saving={updateLayoutMetaMutation.isPending}
        initialValues={
          editingLayout
            ? {
                name: editingLayout.name,
                categoryId: editingLayout.categoryId,
                isDefault: editingLayout.isDefault,
                roleIds: editingLayout.roleIds ?? [],
              }
            : undefined
        }
        onSubmit={(values) => {
          if (editingLayout) {
            updateLayoutMetaMutation.mutate({ layoutId: editingLayout.id, values });
          }
        }}
        onClose={() => setEditingLayout(null)}
      />

      {/* T12.H.11 — confirm usunięcia layoutu */}
      <ConfirmDialog
        open={pendingDeleteLayout !== null}
        title="Usunąć layout?"
        message={
          pendingDeleteLayout
            ? `Czy na pewno usunąć layout „${pendingDeleteLayout.name}"? Tej operacji nie można cofnąć.`
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteLayout) {
            deleteLayoutMutation.mutate(pendingDeleteLayout.id);
          }
        }}
        onClose={() => setPendingDeleteLayout(null)}
      />

      {/* Krok 8 — confirm usunięcia zakładki formularza (sekcje przechodzą do pierwszej) */}
      <ConfirmDialog
        open={pendingDeleteTab !== null}
        title={t('tenant.modules.builder.tabs.deleteTitle')}
        message={
          pendingDeleteTab
            ? t('tenant.modules.builder.tabs.deleteMessage', { name: pendingDeleteTab.title })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteTab) {
            removeFormTab(pendingDeleteTab.id);
            setPendingDeleteTab(null);
          }
        }}
        onClose={() => setPendingDeleteTab(null)}
      />

      {/* T12.H.10 cz.C — side panel ustawień całego layoutu (overlay z prawej; tenant only —
          globalne settings nie są częścią kontraktu formConfig szablonu) */}
      {!isTemplateMode && moduleId && (
        <BuilderSettingsPanel
          open={showSettingsPanel}
          onClose={() => setShowSettingsPanel(false)}
          moduleId={moduleId}
          settings={layoutSettings}
          onSettingsChange={(patch) => {
            setLayoutSettings((prev) => ({ ...prev, ...patch }));
            setDirty(true);
          }}
          fields={activeFields.map((f) => ({
            id: f.id,
            name: f.name,
            slug: f.slug,
            fieldType: f.fieldType,
            onCanvas: usedFieldIds.has(f.id),
          }))}
        />
      )}
    </Wrapper>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeEmptySection(): LayoutSection {
  return {
    id: crypto.randomUUID(),
    title: null,
    columns: 2,
    showHeader: true,
    showBorder: false,
    collapsible: false,
    elements: [],
  };
}

function defaultSections(): LayoutSection[] {
  return [makeEmptySection()];
}

/** Migracja starych layoutów które nie mają showHeader/showBorder/collapsible */
function migrateOldSections(sections: LayoutSection[]): LayoutSection[] {
  return sections.map((s) => ({
    ...s,
    showHeader: s.showHeader ?? true,
    showBorder: s.showBorder ?? false,
    collapsible: s.collapsible ?? false,
  }));
}

// ─── SortableSection ─────────────────────────────────────────────────────────

interface SortableSectionProps {
  section: LayoutSection;
  fields: ModuleField[];
  selected: SelectedTarget;
  onSelectSection: (id: string) => void;
  onSelectElement: (id: string) => void;
  onRemoveElement: (id: string) => void;
  onRemoveSection: (id: string) => void;
  onAddFieldToSection: (sectionId: string) => void;
  onMoveElement: (elementId: string, targetSectionId: string) => void;
  allSections: LayoutSection[];
  canRemoveSection: boolean;
}

function SortableSection({
  section,
  fields,
  selected,
  onSelectSection,
  onSelectElement,
  onRemoveElement,
  onRemoveSection,
  onAddFieldToSection,
  onMoveElement,
  allSections,
  canRemoveSection,
}: SortableSectionProps) {
  const { t } = useTranslation();
  // F12.N D2 — sekcja jest jednocześnie drag source (sort) i drop target dla
  // klocków upuszczanych z palety. `data.target='section'` rozpoznawane przez
  // `usePaletteDnD.onDragEnd` w top-level DndContext.
  const sectionData: SectionDragData = {
    source: 'section',
    target: 'section',
    sectionId: section.id,
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: sectionData,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSectionSelected = selected?.kind === 'section' && selected.sectionId === section.id;

  const colsClass =
    section.columns === 1
      ? 'grid-cols-1'
      : section.columns === 3
        ? 'grid-cols-3'
        : section.columns === 4
          ? 'grid-cols-4'
          : section.columns === 5
            ? 'grid-cols-5'
            : section.columns === 6
              ? 'grid-cols-6'
              : 'grid-cols-2';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-4 rounded-lg border-2 transition-colors ${
        isSectionSelected
          ? 'border-accent-400 bg-accent-50/30'
          : section.showBorder
            ? 'border-border-primary'
            : 'border-transparent'
      }`}
      data-testid={`builder-section-${section.id}`}
    >
      {/* Section header */}
      {section.showHeader && (
        <div
          className={`flex items-center gap-1 rounded-t-md px-3 py-2 ${
            isSectionSelected ? 'bg-accent-100/60' : 'bg-bg-tertiary'
          }`}
          onClick={() => onSelectSection(section.id)}
        >
          {/* Drag handle dla sekcji */}
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={t('tenant.modules.builder.section.drag')}
            className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-primary flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <LayoutGrid className="h-3.5 w-3.5 text-text-muted flex-shrink-0" />

          <span className="flex-1 text-sm font-medium text-text-primary truncate">
            {section.title || t('tenant.modules.builder.section.untitled')}
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddFieldToSection(section.id);
            }}
            aria-label={t('tenant.modules.builder.section.addField')}
            className="text-text-muted hover:text-accent-600 flex-shrink-0"
            title={t('tenant.modules.builder.section.addField')}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>

          {/* Właściwości sekcji — settings icon */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectSection(section.id);
            }}
            aria-label={t('tenant.modules.builder.section.settings')}
            className="text-text-muted hover:text-accent-600 flex-shrink-0"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </button>

          {canRemoveSection && section.elements.length === 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveSection(section.id);
              }}
              aria-label={t('tenant.modules.builder.section.remove')}
              className="text-text-muted hover:text-red-600 flex-shrink-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Elements grid — F12.N D2: tylko SortableContext (DndContext na poziomie strony). */}
      <div className="p-3">
        <SortableContext
          items={section.elements.map((e) => e.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={`grid gap-3 ${colsClass}`}>
            {section.elements.map((element) => {
              const field = fields.find((f) => f.id === element.fieldId);
              return (
                <SortableElement
                  key={element.id}
                  element={element}
                  sectionId={section.id}
                  field={field}
                  selected={selected?.kind === 'element' && selected.elementId === element.id}
                  onSelect={() => onSelectElement(element.id)}
                  onRemove={() => onRemoveElement(element.id)}
                  onMoveToSection={(targetId) => onMoveElement(element.id, targetId)}
                  otherSections={allSections.filter((s) => s.id !== section.id)}
                />
              );
            })}
          </div>
        </SortableContext>

        {section.elements.length === 0 && (
          <div
            className="flex h-16 flex-col items-center justify-center rounded border-2 border-dashed border-border-primary text-text-muted cursor-pointer hover:border-accent-400 hover:text-accent-500 transition-colors"
            onClick={() => onAddFieldToSection(section.id)}
          >
            <span className="text-xs">{t('tenant.modules.builder.section.dropHere')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FieldListItem ────────────────────────────────────────────────────────────

interface FieldListItemProps {
  field: ModuleField;
  disabled?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}

function FieldListItem({ field, disabled, onClick, onDelete, deleting }: FieldListItemProps) {
  const { t } = useTranslation();
  // Krok 6: pole z szablonu jest chronione — usuwanie wyszarzone (backend i tak zwróci 403).
  const isTemplateField = Boolean(field.sourceTemplateModuleId);
  // F12.N D2 — drag&drop: nieużywane pole („disabled=false") jest draggable do canvasu.
  // Już użyte pole („disabled=true") nie da się przeciągnąć (duplikatów nie chcemy).
  const dragData: PaletteDragData = {
    source: 'palette',
    kind: 'field',
    payload: field.id,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-field-${field.id}`,
    data: dragData,
    disabled: Boolean(disabled),
  });

  return (
    <div
      ref={setNodeRef}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      className={
        'group flex items-center gap-2 rounded-md border border-border-primary p-2 text-sm ' +
        (disabled
          ? 'opacity-60'
          : `hover:border-accent-500 hover:bg-bg-tertiary ${isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'}`)
      }
      onClick={disabled ? undefined : onClick}
      data-testid={`builder-field-item-${field.slug}`}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-text-primary">{field.name}</div>
        <div className="truncate text-xs text-text-tertiary">
          {FIELD_TYPE_LABELS[field.fieldType] ?? field.fieldType}
        </div>
      </div>
      {onDelete && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!deleting && !isTemplateField) onDelete();
          }}
          disabled={deleting || isTemplateField}
          aria-label="Usuń pole"
          title={
            isTemplateField ? t('tenant.modules.builder.toasts.fieldTemplateProtected') : undefined
          }
          className={
            isTemplateField
              ? 'cursor-not-allowed text-text-muted opacity-40'
              : 'text-text-muted opacity-0 transition group-hover:opacity-100 hover:text-red-600'
          }
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── SortableElement ──────────────────────────────────────────────────────────

interface SortableElementProps {
  element: LayoutElement;
  /** F12.N D2 — sekcja rodzic; potrzebne żeby palette item upuszczony na element trafił do sekcji. */
  sectionId: string;
  field: ModuleField | undefined;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onMoveToSection: (sectionId: string) => void;
  otherSections: LayoutSection[];
}

function SortableElement({
  element,
  sectionId,
  field,
  selected,
  onSelect,
  onRemove,
  onMoveToSection,
  otherSections,
}: SortableElementProps) {
  const { t } = useTranslation();
  // F12.N D2 — element jest sortowalny + jednocześnie drop target dla palette item
  // (drop trafia do sekcji rodzica przez `data.sectionId`).
  const elementData: ElementDragData = {
    source: 'element',
    target: 'section',
    sectionId,
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
    data: elementData,
  });
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // T12.H.10 — layout element (sekcja/karta/zakładki/itd.) dostaje col-span-full
  // bo to struktura strony, nie pole z width=1/2/3.
  const isLayoutEl = element.type !== 'field';
  const layoutDef = isLayoutEl ? LayoutElementRegistry.get(element.type) : undefined;

  const widthClass = isLayoutEl
    ? 'col-span-full'
    : element.width === 3
      ? 'col-span-3'
      : element.width === 2
        ? 'col-span-2'
        : 'col-span-1';

  const label = isLayoutEl
    ? (layoutDef?.label ?? element.type)
    : (element.settings.label ?? field?.name ?? '???');

  const elementCss = styleToCSS(element.style);
  const hasStyle = element.style && Object.keys(element.style).length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${widthClass} relative rounded-md border-2 bg-bg-primary p-3 ${
        selected ? 'border-accent-500' : 'border-border-primary'
      } hover:border-accent-400 transition-colors cursor-pointer`}
      onClick={onSelect}
      data-testid={`builder-element-${element.id}`}
    >
      {/* Live style preview overlay */}
      {hasStyle && (
        <div className="absolute inset-0 rounded-md pointer-events-none" style={elementCss} />
      )}
      <div className="flex items-start gap-2 relative z-10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={t('common.drag')}
          className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-primary"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text-primary">
            {label}
            {!isLayoutEl && (element.settings.required ?? field?.isRequired) && (
              <span className="ml-1 text-red-500">*</span>
            )}
          </div>
          {isLayoutEl ? (
            <div className="text-xs text-text-tertiary">
              <span className="inline-flex items-center gap-1 rounded bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700">
                Element layoutu
              </span>
              <span className="ml-1.5 text-text-muted">{element.type}</span>
            </div>
          ) : field ? (
            <div className="text-xs text-text-tertiary">
              {FIELD_TYPE_LABELS[field.fieldType] ?? field.fieldType}
            </div>
          ) : null}
          {/* T12.H.10 — preview layout elementu (mode=builder) renderowany pod nagłówkiem */}
          {isLayoutEl && (
            <div className="mt-2 rounded border border-dashed border-border-primary bg-bg-secondary/30 p-2">
              {tryRenderLayoutElement({
                element: {
                  id: element.id,
                  type: element.type,
                  settings: element.settings as Record<string, unknown>,
                  style: element.style as Record<string, unknown> | undefined,
                },
                children: [],
                mode: 'builder',
              })}
            </div>
          )}
        </div>

        {/* Move to section */}
        {otherSections.length > 0 && (
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowMoveMenu((v) => !v);
              }}
              aria-label={t('tenant.modules.builder.element.moveToSection')}
              className="text-text-muted hover:text-accent-600"
              title={t('tenant.modules.builder.element.moveToSection')}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {showMoveMenu && (
              <div className="absolute right-0 top-6 z-20 min-w-[140px] rounded-md border border-border-primary bg-bg-primary shadow-lg">
                <p className="px-3 py-1.5 text-xs font-medium text-text-muted border-b border-border-primary">
                  {t('tenant.modules.builder.element.moveTo')}
                </p>
                {otherSections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs text-text-primary hover:bg-bg-tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoveMenu(false);
                      onMoveToSection(s.id);
                    }}
                  >
                    {s.title || t('tenant.modules.builder.section.untitled')}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t('tenant.modules.builder.element.remove')}
          className="text-text-muted hover:text-red-600 flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── SectionProperties ────────────────────────────────────────────────────────

interface SectionPropertiesProps {
  section: LayoutSection;
  /** Dostępne liczby kolumn — tryb szablonu (hub) ogranicza do 1–3 (kontrakt formConfig). */
  columnsOptions?: readonly (1 | 2 | 3 | 4 | 5 | 6)[];
  /** Krok 8 — rejestr zakładek formularza (pusty = bez selektora przenoszenia). */
  tabs?: FormLayoutTab[];
  onUpdate: (patch: Partial<Omit<LayoutSection, 'id' | 'elements'>>) => void;
}

function SectionProperties({ section, columnsOptions, tabs, onUpdate }: SectionPropertiesProps) {
  const { t } = useTranslation();
  const availableColumns = columnsOptions ?? ([1, 2, 3, 4, 5, 6] as const);

  return (
    <div className="space-y-3" data-testid="builder-section-properties">
      <div className="rounded-md bg-bg-tertiary p-2 text-xs">
        <div className="font-semibold text-text-primary flex items-center gap-1">
          <LayoutGrid className="h-3.5 w-3.5" />
          {t('tenant.modules.builder.section.propertiesTitle')}
        </div>
      </div>

      <FormField label={t('tenant.modules.builder.section.title')}>
        <Input
          value={section.title ?? ''}
          onChange={(e) => onUpdate({ title: e.target.value || null })}
          placeholder={t('tenant.modules.builder.section.titlePlaceholder')}
          data-testid="section-property-title"
        />
      </FormField>

      {/* Krok 8 — przeniesienie sekcji do innej zakładki formularza. */}
      {tabs && tabs.length > 0 && (
        <FormField label={t('tenant.modules.builder.tabs.sectionTab')}>
          <Dropdown
            value={
              section.tabId && tabs.some((x) => x.id === section.tabId)
                ? section.tabId
                : tabs[0]!.id
            }
            onChange={(v) => onUpdate({ tabId: v })}
            data-testid="section-property-tab"
            options={tabs.map((tab) => ({ value: tab.id, label: tab.title }))}
          />
        </FormField>
      )}

      <FormField label={t('tenant.modules.builder.section.columns')}>
        <div className="grid grid-cols-6 gap-1">
          {availableColumns.map((cols) => (
            <button
              key={cols}
              type="button"
              onClick={() => onUpdate({ columns: cols })}
              className={`py-1.5 border rounded text-xs transition ${
                section.columns === cols
                  ? 'border-accent-500 bg-accent-50 text-accent-700 font-medium'
                  : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
              }`}
            >
              {cols}
            </button>
          ))}
        </div>
      </FormField>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={section.showHeader}
            onChange={(e) => onUpdate({ showHeader: e.target.checked })}
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
            data-testid="section-property-show-header"
          />
          <span className="text-text-secondary">
            {t('tenant.modules.builder.section.showHeader')}
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={section.showBorder}
            onChange={(e) => onUpdate({ showBorder: e.target.checked })}
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
            data-testid="section-property-show-border"
          />
          <span className="text-text-secondary">
            {t('tenant.modules.builder.section.showBorder')}
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={section.collapsible}
            onChange={(e) => onUpdate({ collapsible: e.target.checked })}
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
            data-testid="section-property-collapsible"
          />
          <span className="text-text-secondary">
            {t('tenant.modules.builder.section.collapsible')}
          </span>
        </label>
      </div>
    </div>
  );
}

// ─── ElementProperties ────────────────────────────────────────────────────────

interface ElementPropertiesProps {
  element: LayoutElement;
  field: ModuleField;
  sections: LayoutSection[];
  allFields: ModuleField[];
  onUpdateElement: (patch: Partial<LayoutElement>) => void;
  onUpdateSettings: (patch: Partial<LayoutElement['settings']>) => void;
  onMoveToSection: (sectionId: string) => void;
}

const CONDITIONAL_OPERATOR_LABELS: Record<ConditionalOperator, string> = {
  equals: 'równa się',
  not_equals: 'nie równa się',
  not_empty: 'nie jest puste',
  empty: 'jest puste',
};

function ElementProperties({
  element,
  field,
  onUpdateElement,
  onUpdateSettings,
}: ElementPropertiesProps) {
  const { t } = useTranslation();
  const labelOverride = element.settings.label ?? '';
  const requiredOverride = element.settings.required ?? field.isRequired;

  return (
    <div className="space-y-3" data-testid="builder-properties-form">
      <div className="rounded-md bg-bg-tertiary p-2 text-xs">
        <div className="font-semibold text-text-primary">{field.name}</div>
        <div className="text-text-tertiary">
          {FIELD_TYPE_LABELS[field.fieldType] ?? field.fieldType} · /{field.slug}
        </div>
      </div>

      <FormField label={t('tenant.modules.builder.properties.label')}>
        <Input
          value={labelOverride}
          onChange={(e) => onUpdateSettings({ label: e.target.value || null })}
          placeholder={field.name}
          data-testid="builder-property-label"
        />
      </FormField>

      <FormField label={t('tenant.modules.builder.properties.width')}>
        <div className="flex gap-1">
          {[1, 2, 3].map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onUpdateElement({ width: w as 1 | 2 | 3 })}
              aria-pressed={element.width === w}
              className={
                'flex-1 rounded-md border px-2 py-1.5 text-xs transition ' +
                (element.width === w
                  ? 'border-accent-500 bg-accent-500/10 text-accent-600 font-medium'
                  : 'border-border-primary text-text-secondary hover:bg-bg-tertiary')
              }
              data-testid={`builder-property-width-${w}`}
            >
              {w === 1 ? '1/3' : w === 2 ? '2/3' : 'Cała szer.'}
            </button>
          ))}
        </div>
      </FormField>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={requiredOverride}
          onChange={(e) => onUpdateSettings({ required: e.target.checked })}
          data-testid="builder-property-required"
          className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
        />
        <span className="text-text-secondary">
          {t('tenant.modules.builder.properties.required')}
        </span>
      </label>
    </div>
  );
}

// ─── T12.H.9: ConditionalVisibilityPanel — sub-tab „Zaawansowane" ─────────────

interface ConditionalVisibilityPanelProps {
  element: LayoutElement;
  /** ID pola jeśli element to pole (do filtrowania siebie z listy triggerów). */
  currentFieldId?: string;
  allFields: ModuleField[];
  onUpdateSettings: (patch: Record<string, unknown>) => void;
}

function ConditionalVisibilityPanel({
  element,
  currentFieldId,
  allFields,
  onUpdateSettings,
}: ConditionalVisibilityPanelProps) {
  const { t } = useTranslation();
  const hasCondition = !!element.settings.conditionalField;
  const condOperator = element.settings.conditionalOperator ?? 'equals';
  const valueOperators: ConditionalOperator[] = ['equals', 'not_equals'];
  const needsValue = valueOperators.includes(condOperator);

  // Pola dostępne jako trigger — wszystkie poza bieżącym (gdy element to pole)
  const triggerFields = allFields.filter((f) => f.id !== currentFieldId);

  return (
    <div className="space-y-3" data-testid="builder-conditional-section">
      <label className="flex items-center gap-2 text-xs font-semibold text-text-primary">
        <input
          type="checkbox"
          checked={hasCondition}
          onChange={(e) => {
            if (!e.target.checked) {
              onUpdateSettings({
                conditionalField: null,
                conditionalValue: null,
                conditionalOperator: null,
              });
            } else {
              onUpdateSettings({
                conditionalField: triggerFields[0]?.slug ?? null,
                conditionalOperator: 'equals',
                conditionalValue: '',
              });
            }
          }}
          data-testid="builder-property-conditional-toggle"
          className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
        />
        {t('tenant.modules.builder.properties.conditional.title')}
      </label>

      {hasCondition && triggerFields.length > 0 && (
        <div className="space-y-2 pl-6">
          <div>
            <label className="mb-1 block text-xs text-text-secondary">
              {t('tenant.modules.builder.properties.conditional.field')}
            </label>
            <Dropdown
              value={element.settings.conditionalField ?? ''}
              onChange={(v) => onUpdateSettings({ conditionalField: v || null })}
              data-testid="builder-property-conditional-field"
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              options={triggerFields.map((f) => ({ value: f.slug, label: f.name }))}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs text-text-secondary">
              {t('tenant.modules.builder.properties.conditional.operator')}
            </label>
            <Dropdown
              value={condOperator}
              onChange={(v) =>
                onUpdateSettings({
                  conditionalOperator: v as ConditionalOperator,
                })
              }
              data-testid="builder-property-conditional-operator"
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              options={(Object.keys(CONDITIONAL_OPERATOR_LABELS) as ConditionalOperator[]).map(
                (op) => ({ value: op, label: CONDITIONAL_OPERATOR_LABELS[op] }),
              )}
            />
          </div>

          {needsValue && (
            <div>
              <label className="mb-1 block text-xs text-text-secondary">
                {t('tenant.modules.builder.properties.conditional.value')}
              </label>
              <Input
                type="text"
                value={element.settings.conditionalValue ?? ''}
                onChange={(e) => onUpdateSettings({ conditionalValue: e.target.value })}
                data-testid="builder-property-conditional-value"
                placeholder="np. aktywny"
                className="rounded-md px-2 py-1 text-xs"
              />
            </div>
          )}
        </div>
      )}

      {hasCondition && triggerFields.length === 0 && (
        <p className="pl-6 text-xs text-text-muted">Brak innych pól do użycia jako wyzwalacz</p>
      )}
    </div>
  );
}

// ─── T12.H.9: LayoutElementContentTab — sub-tab „Treść" dla layout elementów ──

interface LayoutElementContentTabProps {
  element: LayoutElement;
  onUpdateElement: (patch: Partial<LayoutElement>) => void;
  onUpdateSettings: (patch: Record<string, unknown>) => void;
}

function LayoutElementContentTab({
  element,
  onUpdateElement,
  onUpdateSettings,
}: LayoutElementContentTabProps) {
  const def = LayoutElementRegistry.get(element.type);
  return (
    <div className="space-y-3" data-testid="builder-layout-element-content">
      <div className="rounded-md bg-bg-tertiary p-2 text-xs">
        <div className="font-semibold text-text-primary">{def?.label ?? element.type}</div>
        <div className="text-text-tertiary">Element layoutu · {element.type}</div>
      </div>

      {/* Szerokość elementu w sekcji (wspólne dla layoutu i pól) */}
      <FormField label="Szerokość w sekcji">
        <div className="flex gap-1">
          {[1, 2, 3].map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onUpdateElement({ width: w as 1 | 2 | 3 })}
              aria-pressed={element.width === w}
              className={
                'flex-1 rounded-md border px-2 py-1.5 text-xs transition ' +
                (element.width === w
                  ? 'border-accent-500 bg-accent-500/10 text-accent-600 font-medium'
                  : 'border-border-primary text-text-secondary hover:bg-bg-tertiary')
              }
              data-testid={`builder-layout-width-${w}`}
            >
              {w === 1 ? '1/3' : w === 2 ? '2/3' : 'Cała szer.'}
            </button>
          ))}
        </div>
      </FormField>

      {/* Per-typ editor (jeśli zarejestrowany) */}
      {def?.propertiesEditor ? (
        <div className="border-t border-border-primary pt-3">
          {def.propertiesEditor({
            settings: element.settings as Record<string, unknown>,
            onChange: (patch) => onUpdateSettings(patch),
          })}
        </div>
      ) : (
        <p className="border-t border-border-primary pt-3 text-xs text-text-tertiary">
          Ten element nie ma jeszcze edytora właściwości — wpadnie w T12.H.10.
        </p>
      )}
    </div>
  );
}

// ─── Add field modal ──────────────────────────────────────────────────────────

interface AddFieldModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { name: string; fieldType: string; isRequired: boolean }) => void;
  loading: boolean;
}

function AddFieldModal({ open, onClose, onSubmit, loading }: AddFieldModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [isRequired, setIsRequired] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setFieldType('text');
      setIsRequired(false);
    }
  }, [open]);

  const fieldTypeOptions = useMemo(() => {
    return FieldRegistry.getAll()
      .map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] ?? type }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.modules.builder.addField.title')}
      description={t('tenant.modules.builder.addField.description')}
    >
      <div className="space-y-4">
        <FormField label={t('tenant.modules.builder.addField.fieldName')} required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('tenant.modules.builder.addField.fieldNamePlaceholder')}
            autoFocus
            data-testid="add-field-name-input"
            maxLength={100}
          />
        </FormField>

        <FormField label={t('tenant.modules.builder.addField.fieldType')} required>
          <Dropdown
            value={fieldType}
            onChange={(v) => setFieldType(v)}
            options={fieldTypeOptions}
            data-testid="add-field-type-select"
          />
        </FormField>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
            data-testid="add-field-required-input"
            className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
          />
          <span className="text-text-secondary">
            {t('tenant.modules.builder.addField.required')}
          </span>
        </label>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => onSubmit({ name: name.trim(), fieldType, isRequired })}
          disabled={!name.trim() || loading}
          loading={loading}
          data-testid="add-field-submit"
        >
          {t('common.create')}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * ModuleBuilderPaletteGhost — duszek pola/elementu z palety (parytet z PaletteDragGhost
 * w SystemModuleBuilderPage PR 1603 fix a33d639).
 */
function ModuleBuilderPaletteGhost({
  data,
  fields,
}: {
  data: PaletteDragData;
  fields: ModuleField[];
}) {
  if (data.kind === 'field') {
    const field = fields.find((f) => f.id === data.payload);
    if (!field) return null;
    return (
      <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs shadow-xl">
        <p className="truncate font-medium text-text-primary">{field.name}</p>
        <p className="truncate text-[10px] text-text-tertiary">{field.fieldType}</p>
      </div>
    );
  }
  return (
    <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
      {data.payload}
    </div>
  );
}

function ModuleBuilderElementGhost({
  elementId,
  sections,
  fields,
}: {
  elementId: string;
  sections: LayoutSection[];
  fields: ModuleField[];
}) {
  for (const s of sections) {
    const el = s.elements.find((e) => e.id === elementId);
    if (!el) continue;
    if (el.type === 'field' && el.fieldId) {
      const field = fields.find((f) => f.id === el.fieldId);
      if (field) {
        return (
          <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs shadow-xl">
            <p className="truncate font-medium text-text-primary">{field.name}</p>
            <p className="truncate text-[10px] text-text-tertiary">{field.fieldType}</p>
          </div>
        );
      }
    }
    return (
      <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
        {el.type}
      </div>
    );
  }
  return null;
}

function ModuleBuilderSectionGhost({
  sectionId,
  sections,
}: {
  sectionId: string;
  sections: LayoutSection[];
}) {
  const section = sections.find((s) => s.id === sectionId);
  if (!section) return null;
  return (
    <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-4 py-3 text-sm font-semibold text-text-primary shadow-xl">
      {section.title || 'Sekcja bez tytułu'}
    </div>
  );
}
